/**
 * Ошибки браузера → журнал ошибок prokat (`POST /api/errors`, спека
 * docs/specs/2026-10-27-errors-design.md). Без библиотек: очередь, пачка раз в 5 с,
 * одна и та же ошибка — раз в минуту, не больше 20 событий в минуту с вкладки.
 * Ошибки ответа сервера (5xx) записывает сам сервер — сюда идут падения кода
 * в браузере и «сервис недоступен», которого сервер записать не может.
 */

import { ApiFailure } from "@/lib/api";

declare const __RELEASE__: string;
export const RELEASE = typeof __RELEASE__ === "string" ? __RELEASE__ : "";

type Frame = { file: string; line: number | null; col: number | null; func: string };
type Report = {
  type: string;
  message: string;
  frames: Frame[];
  source: "front" | "ext";
  release: string;
  url: string;
  request_id?: string;
  status?: number;
  code?: string;
  component?: string;
};

const ПАЧКА_МС = 5000;
const В_МИНУТУ = 20;
const ПОВТОР_МС = 60_000;
// Сервис не ответил JSON — отказ зависимости, а не баг кода (сервер его не видит).
const ЗАВИСИМОСТЬ = new Set(["UNAVAILABLE", "BAD_RESPONSE"]);

let source: Report["source"] = "front";
let release = RELEASE;
let send: (path: string, body: string) => void = (path, body) => {
  void fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    credentials: "same-origin",
    keepalive: true,
  }).catch(() => undefined);
};
const queue: Report[] = [];
const seen = new Map<string, number>();
let minute = { at: 0, n: 0 };
let timer: ReturnType<typeof setTimeout> | null = null;

/** Строки стека Chrome (`at f (url:1:2)`) и Firefox (`f@url:1:2`) → кадры. */
export function parseStack(stack: string | undefined): Frame[] {
  const frames: Frame[] = [];
  for (const line of (stack ?? "").split("\n")) {
    const m =
      /^\s*at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?$/.exec(line) ??
      /^(.*?)@(.+?):(\d+):(\d+)$/.exec(line);
    if (!m) continue;
    frames.push({
      func: (m[1] ?? "").slice(0, 200),
      file: ((m[2] ?? "").split("?")[0] ?? "").slice(0, 300),
      line: Number(m[3]),
      col: Number(m[4]),
    });
    if (frames.length >= 30) break;
  }
  return frames;
}

function flush(): void {
  timer = null;
  if (!queue.length) return;
  const events = queue.splice(0, 20);
  send("/api/errors", JSON.stringify({ events }));
  if (queue.length) timer = setTimeout(flush, ПАЧКА_МС);
}

/** Записать ошибку. Сама никогда не бросает. */
export function reportError(err: unknown, extra: { component?: string | undefined } = {}): void {
  try {
    const e = err instanceof Error ? err : new Error(String(err));
    const api = err instanceof ApiFailure ? err : null;
    const report: Report = {
      type: api ? `ApiFailure.${api.code}` : e.name || "Error",
      message: e.message.slice(0, 2000),
      frames: parseStack(e.stack),
      source,
      release,
      url: location.pathname.slice(0, 300),
      ...(api ? { status: api.status, code: api.code } : {}),
      ...(api?.requestId ? { request_id: api.requestId } : {}),
      ...(extra.component ? { component: extra.component.slice(0, 2000) } : {}),
    };
    const key = `${report.type}|${report.message}|${report.frames[0]?.file ?? ""}:${report.frames[0]?.line ?? ""}`;
    const now = Date.now();
    if ((seen.get(key) ?? 0) > now - ПОВТОР_МС) return;
    seen.set(key, now);
    if (now - minute.at > 60_000) minute = { at: now, n: 0 };
    if (++minute.n > В_МИНУТУ) return;
    queue.push(report);
    timer ??= setTimeout(flush, ПАЧКА_МС);
  } catch {
    // Журнал ошибок не должен ронять страницу.
  }
}

/** Отказ запроса после всех повторов: сюда — только то, чего не видел сервер. */
export function reportQueryError(err: unknown): void {
  if (err instanceof ApiFailure) {
    if (ЗАВИСИМОСТЬ.has(err.code)) reportError(err);
    return; // 4xx — не ошибка, 5xx сервер записал сам
  }
  if (err instanceof DOMException && err.name === "AbortError") return;
  if (!navigator.onLine) return;
  reportError(err);
}

/**
 * Глобальные ловушки вкладки. Панель расширения передаёт свою версию (номер расширения)
 * и способ доставки — её запросы идут через воркер.
 */
export function installErrorHooks(
  from: Report["source"],
  options: { release?: string | undefined; transport?: (path: string, body: string) => void } = {},
): void {
  source = from;
  if (options.release) release = options.release;
  if (options.transport) send = options.transport;
  window.addEventListener("error", (e) => reportError(e.error ?? new Error(e.message)));
  window.addEventListener("unhandledrejection", (e) => reportQueryError(e.reason));
  window.addEventListener("pagehide", flush);
}
